export type EcmCriterionDefinition = {
  id: string;
  description: string;
};

export type EcmDefinition = {
  id: string;
  label: string;
  criteria: EcmCriterionDefinition[];
};

export const ECM_DEFINITIONS: EcmDefinition[] = [
  {
    id: 'advanced_power_bars',
    label: 'Advanced power bars',
    criteria: [
      {
        id: 'computer_workstations_present',
        description: 'Are there computer workstations in the building?',
      },
    ],
  },
  {
    id: 'air_curtain',
    label: 'Air curtain',
    criteria: [
      {
        id: 'frequently_opened_doors',
        description:
          'Some bldg doors are opened very frequently or left open for long periods of time (e.g. retail entrance doors or warehouse overhead doors)',
      },
    ],
  },
  {
    id: 'biomass_boiler',
    label: 'Biomass boiler',
    criteria: [
      {
        id: 'customer_requested_biomass',
        description: 'Does the customer request a biomass boiler?',
      },
      {
        id: 'fuel_bunker_space_available',
        description:
          'Is there available space for a fuel bunker adjacent to the boiler room?',
      },
      {
        id: 'monthly_fuel_supply_acceptable',
        description: 'Is a monthly fuel supply acceptable to the customer?',
      },
      {
        id: 'truck_access_for_fuel_delivery',
        description:
          'Is there clear access (for a truck) to the bunker and boiler room for fuel delivery and ash removal?',
      },
    ],
  },
  {
    id: 'boiler_heat_pump_supplement',
    label: 'Boiler heat pump supplement',
    criteria: [
      {
        id: 'main_heating_is_boilers',
        description: 'Main heating system is boiler(s)',
      },
      {
        id: 'no_existing_heat_pump',
        description: 'Bldg does not already have heat pump system',
      },
    ],
  },
  {
    id: 'boiler_setpoint_reduction',
    label: 'Boiler setpoint reduction',
    criteria: [
      {
        id: 'boiler_present',
        description: 'Bldg has boiler(s)',
      },
      {
        id: 'boiler_setpoints_above_134f',
        description: 'Boiler setpoints are >134 °F',
      },
    ],
  },
  {
    id: 'building_automation_system',
    label: 'Building automation system',
    criteria: [
      {
        id: 'no_existing_bas',
        description: 'There is no existing BAS',
      },
    ],
  },
  {
    id: 'carbinx',
    label: 'CarbinX',
    criteria: [
      {
        id: 'gas_or_propane_equipment_present',
        description: 'Bldg has natural gas or propane-fired equipment',
      },
      {
        id: 'equipment_indoors',
        description: 'Equipment is indoors',
      },
      {
        id: 'equipment_input_above_240k_btuh',
        description: 'Equipment has >240,000 btu/h input',
      },
      {
        id: 'space_for_carbinx_unit',
        description:
          'There is enough space in the mechanical room for the CarbinX unit (approx. size 2 refrigerators)',
      },
    ],
  },
  {
    id: 'cogged_v_belts',
    label: 'Cogged V-belts',
    criteria: [
      {
        id: 'fan_motors_with_belts',
        description: 'Fan motors w/ belts present',
      },
      {
        id: 'no_cogged_belts_on_site',
        description: 'No cogged belts found on site',
      },
    ],
  },
  {
    id: 'condensing_upgrade',
    label:
      'Condensing upgrade (boiler, radiant heaters, unit heaters, water heater)',
    criteria: [
      {
        id: 'non_condensing_gas_units_present',
        description:
          'Bldg has non-condensing gas-fired heating units (e.g. boiler, furnace, water heater, unit heater)',
      },
      {
        id: 'drain_near_heating_units',
        description:
          'There is a drain in the vicinity of the heating units (exception is suspended unit heater)',
      },
    ],
  },
  {
    id: 'dhw_recirculation_pump_timer',
    label: 'DHW Recirculation pump timer',
    criteria: [
      {
        id: 'dhw_recirc_pump_present',
        description: 'Bldg has DHW recirc pump',
      },
      {
        id: 'no_existing_pump_timer',
        description: 'Pump does not already have timer',
      },
      {
        id: 'pump_power_below_25w',
        description: 'Pump is <25W',
      },
    ],
  },
  {
    id: 'direct_expansion_air_conditioner',
    label: 'Direct expansion air conditioner',
    criteria: [
      {
        id: 'dx_ac_present',
        description:
          'Bldg has direct expansion AC (i.e. there are condensing units)',
      },
      {
        id: 'condensing_unit_eer_below_12',
        description: 'Condensing units have an EER<12',
      },
    ],
  },
  {
    id: 'electrify',
    label: 'Electrify (boiler, radiant heaters, unit heaters, water heater)',
    criteria: [
      {
        id: 'gas_fired_equipment_present',
        description: 'Bldg has gas-fired equipment',
      },
      {
        id: 'client_interested_in_electrification',
        description:
          'Client has an interest in electrification (e.g. primary goal is GHG savings and not saving money)',
      },
    ],
  },
  {
    id: 'elevator_regenerative_drive',
    label: 'Elevator regenerative drive',
    criteria: [
      {
        id: 'elevator_present',
        description: 'Bldg has elevator',
      },
      {
        id: 'no_existing_regenerative_drive',
        description: 'Elevator does not already have regenerative drive',
      },
    ],
  },
  {
    id: 'energy_star_clothes_washer',
    label: 'Energy star clothes washer',
    criteria: [
      {
        id: 'residential_clothes_washer_present',
        description: 'Bldg has residential clothes washer',
      },
      {
        id: 'washer_not_energy_star',
        description: 'Washer is not an energy star model',
      },
    ],
  },
  {
    id: 'energy_star_dryer',
    label: 'Energy star dryer',
    criteria: [
      {
        id: 'residential_clothes_dryer_present',
        description: 'Bldg has residential clothes dryer',
      },
      {
        id: 'dryer_not_energy_star',
        description: 'Dryer is not an energy star model',
      },
    ],
  },
  {
    id: 'energy_star_refrigerator_and_freezer',
    label: 'Energy star refrigerator and freezer',
    criteria: [
      {
        id: 'residential_fridge_freezer_present',
        description: 'Bldg has residential refrigerator and/or freezer',
      },
      {
        id: 'fridge_freezer_not_energy_star',
        description: 'Refrigerator and/or freezer is not an energy star model',
      },
      {
        id: 'fridge_under_39ft3',
        description: 'Refrigerator does not exceed 39 ft³',
      },
    ],
  },
  {
    id: 'existing_building_commissioning',
    label: 'Existing building commissioning (EBCx)',
    criteria: [
      {
        id: 'building_older_than_10_years',
        description: 'Bldg is not brand new (>10 yrs)',
      },
      {
        id: 'complex_controls_and_equipment',
        description: 'Bldg control systems and equipment are somewhat complex',
      },
      {
        id: 'no_thermal_mass_bas_optimization',
        description: 'Thermal mass BAS optimization ECM is not proposed',
      },
    ],
  },
  {
    id: 'ground_source_heat_pump',
    label: 'Ground source heat pump',
    criteria: [
      {
        id: 'client_requested_gshp',
        description:
          'The client specifically asked to explore a GS heat pump ECM',
      },
      {
        id: 'outdoor_space_for_ground_loops',
        description:
          'There available outdoor space, like a lawn, on the site property',
      },
    ],
  },
  {
    id: 'heat_pump_rooftop_unit',
    label: 'Heat pump rooftop unit',
    criteria: [
      {
        id: 'rtu_present',
        description: 'Bldg has RTU(s)',
      },
      {
        id: 'rtus_not_heat_pumps',
        description: 'RTUs are not already heat pumps',
      },
    ],
  },
  {
    id: 'high_efficiency_chiller',
    label: 'High-efficiency chiller',
    criteria: [
      {
        id: 'chiller_present',
        description: 'Bldg has chiller',
      },
      {
        id: 'chiller_older_than_15_years',
        description: 'Chiller is old (>15 yrs)',
      },
    ],
  },
  {
    id: 'high_efficiency_make_up_air_unit',
    label: 'High-efficiency make-up air unit',
    criteria: [
      {
        id: 'mua_present',
        description: 'Bldg has MUA',
      },
      {
        id: 'mua_indirect_gas_fired',
        description: 'MUA is indirect gas-fired',
      },
      {
        id: 'mua_efficiency_below_91',
        description: 'MUA has an estimated or rated efficiency <91%',
      },
    ],
  },
  {
    id: 'humidity_sensor_switch',
    label: 'Humidity sensor switch',
    criteria: [
      {
        id: 'in_ceiling_exhaust_fans_present',
        description: 'Bldg has in-ceiling exhaust fans',
      },
      {
        id: 'no_existing_humidity_sensors',
        description: 'Exhaust fans do not already have humidity sensors',
      },
    ],
  },
  {
    id: 'hybrid_heat_pump_water_heater',
    label: 'Hybrid heat pump water heater',
    criteria: [
      {
        id: 'electric_water_heater_present',
        description: 'Bldg has electric water heater',
      },
      {
        id: 'water_heater_not_confined',
        description: 'Water heater is not stored in confined space',
      },
      {
        id: 'water_heater_in_heat_rich_space',
        description:
          'Water heater is stored in space where heat is plentiful, e.g., a boiler room',
      },
    ],
  },
  {
    id: 'hydronic_heating_additive',
    label: 'Hydronic heating additive',
    criteria: [
      {
        id: 'hydronic_heating_system_present',
        description: 'Bldg has a hydronic heating system',
      },
    ],
  },
  {
    id: 'indoor_temperature_setpoint_adjustment',
    label: 'Indoor temperature setpoint adjustment',
    criteria: [
      {
        id: 'gas_water_heater_present',
        description: 'Bldg has a gas-fired water heater',
      },
      {
        id: 'no_programmable_thermostats_proposed',
        description:
          'Bldg does not have programmable thermostats and programmable thermostats ECM is not suggested',
      },
      {
        id: 'no_bas_proposed',
        description: 'Bldg does not have BAS and BAS ECM is not suggested',
      },
    ],
  },
  {
    id: 'instantaneous_water_heater',
    label: 'Instantaneous water heater',
    criteria: [
      {
        id: 'gas_water_heater_present',
        description: 'Bldg has gas-fired water heater',
      },
      {
        id: 'water_heater_not_instantaneous',
        description: 'Water heater is not instantaneous',
      },
      {
        id: 'peak_demand_below_11gpm',
        description:
          'Peak demand for serviced water fixtures is <=11 GPM (ideal for small bldgs)',
      },
    ],
  },
  {
    id: 'intelligent_parking_outlets',
    label: 'Intelligent parking outlets',
    criteria: [
      {
        id: 'car_plugs_present',
        description: 'Bldg has car plugs',
      },
      {
        id: 'no_existing_car_plug_controls',
        description: 'Car plugs do not already have controls',
      },
    ],
  },
  {
    id: 'irrigation_controller',
    label: 'Irrigation controller',
    criteria: [
      {
        id: 'irrigation_water_use_present',
        description: 'Bldg consumes water by irrigation',
      },
      {
        id: 'no_existing_irrigation_controller',
        description:
          'Bldg does not already have an irrigation controller (simple clock timer likely used)',
      },
    ],
  },
  {
    id: 'led_exit_signs',
    label: 'LED Exit signs',
    criteria: [
      {
        id: 'non_led_exit_signs_present',
        description: 'Bldg has non-LED exit signs',
      },
    ],
  },
  {
    id: 'led_lighting',
    label: 'LED Lighting',
    criteria: [
      {
        id: 'non_led_fixtures_present',
        description: 'Bldg has non-LED fixtures',
      },
    ],
  },
  {
    id: 'lighting_controls',
    label: 'Lighting controls',
    criteria: [
      {
        id: 'basic_manual_lighting_switches',
        description: 'Bldg lighting uses primarily basic manual switches',
      },
      {
        id: 'spaces_benefit_from_dimming_or_occupancy',
        description:
          'Bldg spaces would benefit from dimming or occupancy controls',
      },
    ],
  },
  {
    id: 'liquid_pool_cover',
    label: 'Liquid pool cover',
    criteria: [
      {
        id: 'indoor_pool_present',
        description: 'Bldg has an indoor pool',
      },
      {
        id: 'no_existing_pool_cover',
        description:
          'Pool does not already have a physical (mat) or chemical cover',
      },
    ],
  },
  {
    id: 'low_flow_water_fixtures',
    label: 'Low flow water fixtures',
    criteria: [
      {
        id: 'water_fixtures_present',
        description: 'Bldg has water fixtures',
      },
      {
        id: 'inefficient_water_fixtures_present',
        description: 'Water fixtures are not all efficient',
      },
    ],
  },
  {
    id: 'mini_split_heat_pump',
    label:
      'Mini-split heat pump (electric baseboard, electric or gas unit heaters)',
    criteria: [
      {
        id: 'no_boiler_furnace_or_rtu',
        description:
          'Building does not have an existing boiler, furnace, or RTUs',
      },
      {
        id: 'no_existing_heat_pump_system',
        description: 'Building does not already have a heat pump system',
      },
      {
        id: 'space_for_mini_split_units',
        description:
          'Building has space on wall for interior evaporator unit and space on exterior for associated compressor units',
      },
    ],
  },
  {
    id: 'open_refrigerator_night_cover',
    label: 'Open refrigerator night cover',
    criteria: [
      {
        id: 'open_refrigerators_present',
        description: 'Bldg has open refrigerators or deli fridges',
      },
      {
        id: 'no_existing_fridge_covers',
        description:
          'There are no covers for the fridges, used during unoccupied hours',
      },
    ],
  },
  {
    id: 'pipe_insulation',
    label: 'Pipe insulation',
    criteria: [
      {
        id: 'uninsulated_hot_piping_present',
        description: 'Bldg has uninsulated hot piping in mechanical rm(s)',
      },
    ],
  },
  {
    id: 'programmable_thermostats',
    label: 'Programmable thermostats',
    criteria: [
      {
        id: 'analog_thermostats_present',
        description: 'Bldg has analog thermostats',
      },
      {
        id: 'no_bas_ecm_proposed',
        description: 'BAS ECM not suggested',
      },
    ],
  },
  {
    id: 'realice',
    label: 'REALice',
    criteria: [
      {
        id: 'ice_rink_present',
        description: 'Bldg has ice rink',
      },
      {
        id: 'no_existing_realice',
        description: 'Bldg does not already have REALice tech',
      },
    ],
  },
  {
    id: 'roof_insulation',
    label: 'Roof insulation',
    criteria: [
      {
        id: 'client_requested_or_attic_present',
        description:
          'Client has specifically asked for roof insulation ecm OR bldg has attic',
      },
    ],
  },
  {
    id: 'rooftop_solar',
    label: 'Rooftop solar',
    criteria: [
      {
        id: 'building_shorter_than_8_stories',
        description: 'Bldg is shorter than eight stories',
      },
      {
        id: 'south_facing_roof',
        description: 'Bldg has south-facing roof',
      },
      {
        id: 'roof_space_for_panels',
        description: 'Roof has space for panels',
      },
      {
        id: 'roof_not_obstructed',
        description: 'Roof is not obstructed',
      },
      {
        id: 'not_steel_shed_structure',
        description: 'Bldg is not a simple steel-frame shed type structure',
      },
    ],
  },
  {
    id: 'solar_air_heater',
    label: 'Solar air heater',
    criteria: [
      {
        id: 'air_heating_present',
        description: 'Bldg has air heating',
      },
      {
        id: 'space_for_solar_air_heater',
        description:
          'There is good available space on roof or wall (non-obstructed, south-facing, no windows)',
      },
    ],
  },
  {
    id: 'solar_carport',
    label: 'Solar carport',
    criteria: [
      {
        id: 'rooftop_solar_not_proposed_or_insufficient',
        description:
          "Rooftop solar is not proposed or rooftop solar does not meet bldg's baseload demand",
      },
      {
        id: 'exterior_parking_area_present',
        description: 'Building has exterior parking area',
      },
      {
        id: 'parking_area_unobstructed',
        description: 'Parking area is open and unobstructed',
      },
      {
        id: 'parking_area_southern_exposure',
        description: 'Parking area has southern exposure',
      },
    ],
  },
  {
    id: 'solar_facade',
    label: 'Solar façade',
    criteria: [
      {
        id: 'rooftop_solar_or_carport_insufficient',
        description:
          'Rooftop solar or solar carport are not suggested OR highest monthly output from proposed rooftop solar or solar carport does not meet monthly baseload.',
      },
      {
        id: 'abundant_south_east_west_wall_space',
        description:
          'Bldg has abundant wall space on east, south or west walls, i.e. not window curtain wall, ideally a wall with no windows',
      },
    ],
  },
  {
    id: 'solar_water_heater',
    label: 'Solar water heater',
    criteria: [
      {
        id: 'dhw_system_present',
        description: 'Bldg has DHW system',
      },
      {
        id: 'roof_space_available',
        description: 'Bldg has available roof space',
      },
      {
        id: 'roof_not_entirely_north_facing',
        description: 'Roof space is not entirely north facing',
      },
      {
        id: 'roof_generally_not_shaded',
        description: 'Roof space is generally not shaded',
      },
    ],
  },
  {
    id: 'thermal_mass_bas_optimization',
    label: 'Thermal mass BAS optimization',
    criteria: [
      {
        id: 'bas_present_or_proposed',
        description:
          'Bldg has BAS or BAS ECM is suggested within study (can be implemented in conjunction w/ BAS ECM)',
      },
      {
        id: 'building_is_large',
        description: 'Bldg is large',
      },
      {
        id: 'centralized_heating_cooling',
        description:
          'Bldg is heated and/or cooled by centralized systems (e.g. not multiple RTUs with different zones)',
      },
    ],
  },
  {
    id: 'through_wall_heat_recovery_ventilator',
    label: 'Through-wall heat recovery ventilator',
    criteria: [
      {
        id: 'client_requested_twhrv',
        description: 'Client has specifically requested this measure',
      },
      {
        id: 'hydronic_heating_system_present',
        description: 'Bldg has a hydronic heating system',
      },
    ],
  },
  {
    id: 'variable_frequency_drive',
    label: 'Variable frequency drive',
    criteria: [
      {
        id: 'motors_above_5hp',
        description: 'Bldg has motors >= 5hp',
      },
      {
        id: 'motors_have_variable_loads',
        description:
          'Motors have variable loads (eg VAV system for fan motors)',
      },
      {
        id: 'no_existing_vfds',
        description: 'Motors do not already have VFDs',
      },
      {
        id: 'pump_zone_valves_two_way',
        description: 'For pumps, zone valves are in two-way configuration',
      },
    ],
  },
  {
    id: 'vending_misers',
    label: 'Vending misers',
    criteria: [
      {
        id: 'drink_vending_machines_present',
        description:
          'Building has drink vending machines (i.e. w/ non-perishables, cooling)',
      },
      {
        id: 'no_existing_vending_misers',
        description:
          'Vending machines are not already equipped with vending misers',
      },
    ],
  },
  {
    id: 'wall_insulation',
    label: 'Wall insulation',
    criteria: [
      {
        id: 'client_requested_wall_insulation',
        description: 'Client has specifically asked for wall insulation ECM',
      },
    ],
  },
  {
    id: 'water_flow_sensor',
    label: 'Water flow sensor',
    criteria: [
      {
        id: 'leaks_suspected_or_reported',
        description:
          'Bldg operator indicated leaks are a problem or you suspect leaks are a problem',
      },
    ],
  },
  {
    id: 'water_heater_insulation',
    label: 'Water heater insulation',
    criteria: [
      {
        id: 'electric_water_heater_insulation_eligible',
        description:
          'Building contains a 40 gallon, 60 gallon, or 80 gallon electric resistance domestic hot water heater. Alternatively, an electric resistance heater is being recommended as an ECM. If a hybrid heat pump water heater is being recommended this ECM should be a measure for additional consideration. Heat pump water heaters often have higher thermal resistance values in the storage tank. Further, all associated venting with the heat pump portion of the tank must remain unobstructed. Water heater is accessible and it will be possible to insulate the full water heater',
      },
    ],
  },
  {
    id: 'weather_stripping',
    label: 'Weather stripping',
    criteria: [
      {
        id: 'drafty_windows_or_doors',
        description:
          'Are there existing (operable) windows/doors exhibiting noticeable drafts or requiring sealing?',
      },
    ],
  },
  {
    id: 'window_film',
    label: 'Window film',
    criteria: [
      {
        id: 'large_window_area',
        description:
          'Building has 10,000+ sqft window area. (this can be explored for smaller buildings if explicitly requested)',
      },
      {
        id: 'high_window_to_wall_ratio',
        description:
          'Building has 50%+ window to wall ratio. (this can be explored for smaller buildings if explicitly requested)',
      },
      {
        id: 'windows_not_triple_pane',
        description: 'Windows are not triple pane.',
      },
      {
        id: 'windows_flush_to_surface',
        description:
          'Windows are flush to the surface and not recessed in masonry or concrete',
      },
    ],
  },
  {
    id: 'window_upgrade',
    label: 'Window upgrade',
    criteria: [
      {
        id: 'client_requested_or_single_pane_windows',
        description:
          'Client has specifically asked for window upgrade ECM OR windows are largely single pane',
      },
    ],
  },
];
